/**
 * Route definitions shared between client (createBrowserRouter)
 * and server (createStaticHandler / createStaticRouter) entry points.
 *
 * Uses react-router v6 RouteObject[] shape.
 */
import type { RouteObject } from "react-router-dom";
import { AppShellV2 } from "./components/v2/AppShellV2.tsx";
import { AuthLayout } from "./components/layout/AuthLayout.tsx";
import { ProtectedRoute } from "./components/layout/ProtectedRoute.tsx";

import { LoginPage } from "./pages/LoginPage.tsx";
import { SignupPage } from "./pages/SignupPage.tsx";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage.tsx";
import { ResetPasswordPage } from "./pages/ResetPasswordPage.tsx";
import { NotFoundPage } from "./pages/NotFoundPage.tsx";
import { OtpPage } from "./pages/OtpPage.tsx";
import { DashboardPage } from "./pages/DashboardPage.tsx";
import { NewVideoPage } from "./pages/NewVideoPage.tsx";
import { ScriptReviewPage } from "./pages/ScriptReviewPage.tsx";
import { StoryboardReviewPage } from "./pages/StoryboardReviewPage.tsx";
import { RenderProgressPage } from "./pages/RenderProgressPage.tsx";
import { VideoDetailPage } from "./pages/VideoDetailPage.tsx";
import { VideosListPage } from "./pages/VideosListPage.tsx";
import { BillingPage } from "./pages/BillingPage.tsx";
import { SubscribePage } from "./pages/billing/SubscribePage.tsx";
import { SettingsPage } from "./pages/SettingsPage.tsx";
import { DataPrivacyPage } from "./pages/DataPrivacyPage.tsx";   // (tf3-34) Data and privacy
import { AnalyticsPage } from "./pages/AnalyticsPage.tsx";
import { AdminLayout } from "./pages/admin/AdminLayout.tsx";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage.tsx";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage.tsx";
import { AdminGatewaysPage } from "./pages/admin/AdminGatewaysPage.tsx";
import { AdminProvidersPage } from "./pages/admin/AdminProvidersPage.tsx";
import { AdminSettingsPage } from "./pages/admin/AdminSettingsPage.tsx";
import { AdminAuditPage } from "./pages/admin/AdminAuditPage.tsx";
import { AdminPresetsPage } from "./pages/admin/AdminPresetsPage.tsx";
import { AdminVideoTypesPage } from "./pages/admin/AdminVideoTypesPage.tsx";   // session 7 3.8
import { AdminCaptionStylesPage } from "./pages/admin/AdminCaptionStylesPage.tsx";
import { AdminStockAvatarsPage } from "./pages/admin/AdminStockAvatarsPage.tsx";
import { AdminCharactersPage } from "./pages/admin/AdminCharactersPage.tsx";
import { AdminCastingPage } from "./pages/admin/AdminCastingPage.tsx";
import { AdminGrowthPage } from "./pages/admin/AdminGrowthPage.tsx";
import { AdminTenantsPage } from "./pages/admin/AdminTenantsPage.tsx";
import { AdminApiKeysPage } from "./pages/admin/AdminApiKeysPage.tsx";
import { AdminFeedbackPage } from "./pages/admin/AdminFeedbackPage.tsx";
import { AdminPromosPage } from "./pages/admin/AdminPromosPage.tsx";
import { AdminReferralsPage } from "./pages/admin/AdminReferralsPage.tsx";
import { AdminShowcasePage } from "./pages/admin/AdminShowcasePage.tsx";
import { AdminAttributionPage } from "./pages/admin/AdminAttributionPage.tsx";
import { AdminVideosPage } from "./pages/admin/AdminVideosPage.tsx";
import { AdminResearchPage } from "./pages/admin/AdminResearchPage.tsx";
import { AdminResearchArcsPage } from "./pages/admin/AdminResearchArcsPage.tsx";
import { AdminGrowthArcsPage } from "./pages/admin/AdminGrowthArcsPage.tsx";
import { AdminGrowthQueuePage } from "./pages/admin/AdminGrowthQueuePage.tsx";
import { AdminOperationsPage } from "./pages/admin/AdminOperationsPage.tsx";
import { AdminGrowthSpreadReviewPage } from "./pages/admin/AdminGrowthSpreadReviewPage.tsx";
import { AdminRunInspectorPage } from "./pages/admin/AdminRunInspectorPage.tsx";
import { AdminSeriesPage } from "./pages/admin/AdminSeriesPage.tsx";   // (TF-6b-2) Admin → Series
import { AdminSeriesNewPage } from "./pages/admin/AdminSeriesNewPage.tsx";
import { AdminSeriesDetailPage } from "./pages/admin/AdminSeriesDetailPage.tsx";
import { AdminSlatesPage } from "./pages/admin/AdminSlatesPage.tsx";   // (TF-6b-3) Admin → Slates
import { AdminSlatesNewPage } from "./pages/admin/AdminSlatesNewPage.tsx";
import { AdminSlateBoardPage } from "./pages/admin/AdminSlateBoardPage.tsx";
import { AdminSlateLearningPage } from "./pages/admin/AdminSlateLearningPage.tsx";   // (tf3-42)
import { AdminAudiencesPage } from "./pages/admin/AdminAudiencesPage.tsx";   // (tf3-42)
import { AdminCastPage } from "./pages/admin/AdminCastPage.tsx";   // (B101)
import { AdminWordsPage } from "./pages/admin/AdminWordsPage.tsx";   // (B107) Admin → Words
import { AdminWaitingPage } from "./pages/admin/AdminWaitingPage.tsx";   // (TF-6b-4) Waiting on you
import { AdminEngineStatePage } from "./pages/admin/AdminEngineStatePage.tsx";   // (session 9) Admin → Engine state
import { AdminTeardownsPage } from "./pages/admin/AdminTeardownsPage.tsx";
import { AdminEngineConsolePage } from "./pages/admin/AdminEngineConsolePage";
import { TeamAcceptPage } from "./pages/TeamAcceptPage.tsx";
import { ReferralPage } from "./pages/ReferralPage.tsx";
import { ChannelsListPage } from "./pages/ChannelsListPage.tsx";
import { ChannelDetailPage } from "./pages/ChannelDetailPage.tsx";
import { AssetsPage } from "./pages/AssetsPage.tsx";
import { StrategyPage } from "./pages/StrategyPage.tsx";
import { PublicLayout } from "./components/seo/PublicLayout.tsx";
import { FeaturesPage } from "./pages/public/FeaturesPage.tsx";
import { HowItWorksPage } from "./pages/public/HowItWorksPage.tsx";
import { PricingPage } from "./pages/public/PricingPage.tsx";
import { UseCasesPage } from "./pages/public/UseCasesPage.tsx";
import { AboutPage } from "./pages/public/AboutPage.tsx";
import { BlogIndexPage } from "./pages/public/BlogIndexPage.tsx";
import { BlogPostPage } from "./pages/public/BlogPostPage.tsx";
import { HelpIndexPage } from "./pages/public/HelpIndexPage.tsx";
import { HelpArticlePage } from "./pages/public/HelpArticlePage.tsx";
import { PrivacyPage } from "./pages/public/PrivacyPage.tsx";
import { DataDeletionStatusPage } from "./pages/public/DataDeletionStatusPage.tsx";   // (tf3-34b)
import { TermsPage } from "./pages/public/TermsPage.tsx";
import { DataDeletionPage, ContactPage } from "./pages/public/PendingTextPages.tsx";   // (B120)
import { WizardV1, WizardV2, WizardV3 } from "./pages/onboarding/wizards.tsx";
import { LandingABRouter, LandingVariantPreview } from "./pages/landing-variants/ABRouter.tsx";

/** Routes that should be pre-rendered at build time for SEO. */
export const PUBLIC_PRERENDER_PATHS = [
  "/",
  "/home-a",
  "/home-b",
  "/home-c",
  "/home-d",
  "/onboarding",
  "/onboarding/v2",
  "/onboarding/v3",
  "/features",
  "/how-it-works",
  "/pricing",
  "/use-cases",
  "/about",
  "/privacy",
  "/terms",
  "/data-deletion",   // (B120) pre-rendered: the raw HTML holds the page
  "/contact",
  "/blog",
  "/blog/what-is-a-faceless-youtube-channel",
  "/blog/best-niches-for-faceless-youtube",
  "/blog/ai-video-generation-workflow",
  "/blog/youtube-shorts-vs-long-form",
  "/blog/ai-voiceover-comparison",
  "/blog/how-to-write-hooks-that-keep-viewers",
  "/help",
  "/help/what-is-tubeforge",
  "/help/creating-your-first-video",
  "/help/understanding-credits",
  "/help/connecting-youtube-channel",
  "/help/how-script-generation-works",
  "/help/editing-regenerating-script",
  "/help/storyboard-review",
  "/help/render-progress",
  "/help/downloading-video",
  "/help/publishing-to-youtube",
  "/help/what-is-a-series",
  "/help/creating-a-series",
  "/help/pausing-resuming-series",
  "/help/series-frequency-options",
  "/help/connecting-tiktok",
  "/help/scheduled-publishing",
  "/help/uploading-assets",
  "/help/importing-asset-from-url",
  "/help/what-is-the-strategy-tool",
  "/help/creating-videos-from-strategy",
  "/help/plan-comparison",
  "/help/upgrading-downgrading",
  "/help/cancellation-refunds",
  "/help/profile-and-settings",
  "/help/inviting-team-members",
  "/help/referral-program",
  "/help/video-stuck-generating",
  "/help/youtube-publish-failed",
  "/help/render-errors",
  "/help/storyboard-image-failed",
];

export const routes: RouteObject[] = [
  // Public — A/B tested landing page
  { path: "/", element: <LandingABRouter /> },

  // Landing variant previews (for QA / direct access)
  { path: "/home-a", element: <LandingVariantPreview variant="a" /> },
  { path: "/home-b", element: <LandingVariantPreview variant="b" /> },
  { path: "/home-c", element: <LandingVariantPreview variant="c" /> },
  { path: "/home-d", element: <LandingVariantPreview variant="d" /> },

  // Onboarding wizard (outside app shell)
  { path: "/onboarding", element: <WizardV1 /> },
  { path: "/onboarding/v2", element: <WizardV2 /> },
  { path: "/onboarding/v3", element: <WizardV3 /> },

  // Public marketing/SEO pages (shared nav + footer)
  {
    element: <PublicLayout />,
    children: [
      { path: "/features", element: <FeaturesPage /> },
      { path: "/how-it-works", element: <HowItWorksPage /> },
      { path: "/pricing", element: <PricingPage /> },
      { path: "/use-cases", element: <UseCasesPage /> },
      { path: "/about", element: <AboutPage /> },
      { path: "/blog", element: <BlogIndexPage /> },
      { path: "/blog/:slug", element: <BlogPostPage /> },
      { path: "/help", element: <HelpIndexPage /> },
      { path: "/help/:slug", element: <HelpArticlePage /> },
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/terms", element: <TermsPage /> },
      { path: "/data-deletion/:code", element: <DataDeletionStatusPage /> },
      { path: "/data-deletion", element: <DataDeletionPage /> },   // (B120; planning 10-27 07:20) its words come with B120-1
      { path: "/contact", element: <ContactPage /> },
    ],
  },

  // Auth (split panel layout)
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/signup", element: <SignupPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/reset-password", element: <ResetPasswordPage /> },
    ],
  },

  // OTP page (standalone — used both pre-auth for OTP login and post-auth for email verification)
  { path: "/otp", element: <OtpPage /> },

  // Protected user routes
  {
    element: (
      <ProtectedRoute>
        <AppShellV2 />
      </ProtectedRoute>
    ),
    children: [
      { path: "/dashboard", element: <DashboardPage /> },
      { path: "/videos", element: <VideosListPage /> },
      { path: "/videos/new", element: <NewVideoPage /> },
      { path: "/videos/:id", element: <VideoDetailPage /> },
      { path: "/videos/:id/script", element: <ScriptReviewPage /> },
      { path: "/videos/:id/storyboard", element: <StoryboardReviewPage /> },
      { path: "/videos/:id/render", element: <RenderProgressPage /> },
      { path: "/billing", element: <BillingPage /> },
      { path: "/subscribe", element: <SubscribePage /> },
      { path: "/analytics", element: <AnalyticsPage /> },
      { path: "/channels", element: <ChannelsListPage /> },
      { path: "/channels/:channelId", element: <ChannelDetailPage /> },
      { path: "/assets", element: <AssetsPage /> },
      { path: "/strategy", element: <StrategyPage /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/settings/privacy", element: <DataPrivacyPage /> },
      { path: "/team/accept", element: <TeamAcceptPage /> },
      { path: "/referrals", element: <ReferralPage /> },
    ],
  },

  // Admin
  {
    element: (
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: "/admin", element: <AdminDashboardPage /> },
      { path: "/admin/videos", element: <AdminVideosPage /> },
      { path: "/admin/research", element: <AdminResearchPage /> },
      { path: "/admin/research-arcs", element: <AdminResearchArcsPage /> },
      { path: "/admin/growth/queue", element: <AdminGrowthQueuePage /> },
      { path: "/admin/operations", element: <AdminOperationsPage /> },
      { path: "/admin/words", element: <AdminWordsPage /> },   // (B107) the customer's words
      { path: "/admin/growth/arcs/:arcId/review/:kind", element: <AdminGrowthSpreadReviewPage /> },
      { path: "/admin/growth/arcs/:arcId", element: <AdminGrowthArcsPage /> },
      { path: "/admin/runs", element: <AdminRunInspectorPage /> },
      { path: "/admin/series", element: <AdminSeriesPage /> },
      { path: "/admin/series/new", element: <AdminSeriesNewPage /> },
      { path: "/admin/series/:id", element: <AdminSeriesDetailPage /> },
      { path: "/admin/slates", element: <AdminSlatesPage /> },
      { path: "/admin/slates/new", element: <AdminSlatesNewPage /> },
      { path: "/admin/slates/learning", element: <AdminSlateLearningPage /> },   // (tf3-42) before :id
      { path: "/admin/slates/:id", element: <AdminSlateBoardPage /> },
      { path: "/admin/audiences", element: <AdminAudiencesPage /> },   // (tf3-42)
      { path: "/admin/cast", element: <AdminCastPage /> },   // (B101) the cast candidates picker
      { path: "/admin/cast/:id", element: <AdminCastPage /> },
      { path: "/admin/waiting", element: <AdminWaitingPage /> },
      { path: "/admin/engine-state", element: <AdminEngineStatePage /> },
      { path: "/admin/teardowns", element: <AdminTeardownsPage /> },
      { path: "/admin/engine-console", element: <AdminEngineConsolePage /> },
      { path: "/admin/users", element: <AdminUsersPage /> },
      { path: "/admin/gateways", element: <AdminGatewaysPage /> },
      { path: "/admin/providers", element: <AdminProvidersPage /> },
      { path: "/admin/presets", element: <AdminPresetsPage /> },
      { path: "/admin/video-types", element: <AdminVideoTypesPage /> },
      { path: "/admin/caption-styles", element: <AdminCaptionStylesPage /> },
      { path: "/admin/stock-avatars", element: <AdminStockAvatarsPage /> },
      { path: "/admin/characters", element: <AdminCharactersPage /> },
      { path: "/admin/casting", element: <AdminCastingPage /> },
      { path: "/admin/growth", element: <AdminGrowthPage /> },
      { path: "/admin/tenants", element: <AdminTenantsPage /> },
      { path: "/admin/api-keys", element: <AdminApiKeysPage /> },
      { path: "/admin/feedback", element: <AdminFeedbackPage /> },
      { path: "/admin/promos", element: <AdminPromosPage /> },
      { path: "/admin/referrals", element: <AdminReferralsPage /> },
      { path: "/admin/showcase", element: <AdminShowcasePage /> },
      { path: "/admin/attribution", element: <AdminAttributionPage /> },
      { path: "/admin/settings", element: <AdminSettingsPage /> },
      { path: "/admin/audit", element: <AdminAuditPage /> },
    ],
  },

  // 404
  { path: "*", element: <NotFoundPage /> },
];
